// ============================================
// P0-③ 场景树视图（2026-10-15）
// 分类：相机 / 灯光 / 模型 / 天空·环境（子系统按激活出现）
// 交互：点选（3D↔树双向联动）/ 眼睛显隐 / 子系统跳面板 / 右键菜单（重命名·隐藏·定位·进入相机·删除）
//       双击重命名 / Ctrl·Shift 多选 + 编组·解散 / 拖拽重排模型（antd Tree draggable）
// 见 P0 架构 §2.2/§2.3/§2.5
// ============================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Tree, Input, Button, Space, Typography, Tooltip, Popconfirm, Empty, message } from 'antd';
import type { DataNode } from 'antd/es/tree';
import {
  SearchOutlined,
  BoxPlotOutlined,
  BulbOutlined,
  VideoCameraOutlined,
  FolderOutlined,
  GlobalOutlined,
  CloudOutlined,
  NodeIndexOutlined,
  BorderOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  GroupOutlined,
  UngroupOutlined,
  AimOutlined,
  AppstoreOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import useSceneStore from '../../stores/sceneStore';
import { useSelectionStore } from '../../stores/selectionStore';
import { useRoamStore } from '../../stores/roamStore';
import useEditorUiStore from '../../stores/editorUiStore';
import useLayerStore from '../../stores/layerStore';
import { updateModel as apiUpdateModel } from '../../api/models';
import { deleteModelsByIds } from '../../utils/modelActions';
import { buildSceneTree, flattenTree, modelOrderInTree, type SceneTreeNode, type SceneTreeInput } from '../../utils/sceneTree';
import { reorderByDrop } from '../dashboard/layerDrag';
import { mirrorWaypointsFromCameras } from '../../utils/cameraPathMirror';
import { cameraControlsRegistry } from '../../stores/cameraControlsStore';
import SceneTreeContextMenu, { type ContextMenuAction } from './SceneTreeContextMenu';
import { readSavedPanelPrefs, writeSavedPanelPrefs } from '../../hooks/usePanelPersistence';

const { Text } = Typography;

const ICONS: Record<string, React.ReactNode> = {
  model: <BoxPlotOutlined style={{ color: '#1890ff' }} />,
  light: <BulbOutlined style={{ color: '#faad14' }} />,
  camera: <VideoCameraOutlined style={{ color: '#13c2c2' }} />,
  group: <FolderOutlined style={{ color: '#faad14' }} />,
  subsystem: <GlobalOutlined style={{ color: '#9254de' }} />,
  sky: <CloudOutlined style={{ color: '#40a9ff' }} />,
  fog: <CloudOutlined style={{ color: '#8c8c8c' }} />,
  weather: <CloudOutlined style={{ color: '#69c0ff' }} />,
  roam: <VideoCameraOutlined style={{ color: '#13c2c2' }} />,
  route: <NodeIndexOutlined style={{ color: '#52c41a' }} />,
  region: <BorderOutlined style={{ color: '#fa8c16' }} />,
  dayNight: <ClockCircleOutlined style={{ color: '#597ef7' }} />,
  measure: <AimOutlined style={{ color: '#722ed1' }} />,
  scene: <AppstoreOutlined style={{ color: '#40a9ff' }} />,
  chart3d: <BarChartOutlined style={{ color: '#36cfc9' }} />,
};

/** 过滤树（保留命中节点及其祖先）；命中分类/组时保留全部子节点 */
function filterTree(nodes: SceneTreeNode[], q: string): SceneTreeNode[] {
  const query = q.trim().toLowerCase();
  if (!query) return nodes;
  const walk = (list: SceneTreeNode[]): SceneTreeNode[] => {
    const out: SceneTreeNode[] = [];
    list.forEach((n) => {
      const selfHit = n.label.toLowerCase().includes(query);
      const kids = n.children ? walk(n.children) : undefined;
      if (selfHit) {
        out.push({ ...n, children: n.children });
      } else if (kids && kids.length) {
        out.push({ ...n, children: kids });
      }
    });
    return out;
  };
  return walk(nodes);
}

export default function SceneTreeView() {
  const models = useSceneStore((s) => s.models);
  const lights = useSceneStore((s) => s.lights);
  const pathCameras = useSceneStore((s) => s.pathCameras);
  const browseRoute = useSceneStore((s) => s.browseRoute);
  const displayRegion = useSceneStore((s) => s.displayRegion);
  const measurements = useSceneStore((s) => s.measurements);
  const effects = useSceneStore((s) => s.effects);
  const environment = useSceneStore((s) => s.environment);
  const selectedModelId = useSceneStore((s) => s.selectedModelId);
  const selectModel = useSceneStore((s) => s.selectModel);
  const updateModel = useSceneStore((s) => s.updateModel);
  const removeModel = useSceneStore((s) => s.removeModel);
  const setModels = useSceneStore((s) => s.setModels);
  const updateLight = useSceneStore((s) => s.updateLight);
  const removeLight = useSceneStore((s) => s.removeLight);
  const removePathCamera = useSceneStore((s) => s.removePathCamera);
  const setBrowseRoute = useSceneStore((s) => s.setBrowseRoute);
  const setDisplayRegion = useSceneStore((s) => s.setDisplayRegion);
  const setEffects = useSceneStore((s) => s.setEffects);
  const setEnvironment = useSceneStore((s) => s.setEnvironment);
  const setActivePathCamera = useRoamStore((s) => s.setActivePathCamera);
  const setActivePanel = useEditorUiStore((s) => s.setActivePanel);
  const sceneId = useSceneStore((s) => s.currentScene?.id);
  // 2026-10-15（P0-⑤ §8.2）：灯光选中联动（场景 gizmo ↔ 树）
  const lightSelectedId = useSelectionStore((s) => s.lightSelectedId);
  const selectLight = useSelectionStore((s) => s.selectLight);
  // 2026-10-15（P0-④）：鸟瞰相机（节点仅在启用时出现；点击进入属性面板）
  const birdsEyeCamera = useSceneStore((s) => s.birdsEyeCamera);
  const mainCamera = useSceneStore((s) => s.mainCamera);
  // 2026-10-17（体积元素 S1）
  const volumes = useSceneStore((s) => s.volumes);
  const annotations = useSceneStore((s) => s.annotations);
  const charts3d = useSceneStore((s) => s.chart3dObjects);
  const chart3dSelectedId = useSelectionStore((s) => s.chart3dSelectedId);

  const layers = useLayerStore((s) => s.layers);
  const addLayer = useLayerStore((s) => s.addLayer);
  const removeLayer = useLayerStore((s) => s.removeLayer);
  const updateLayer = useLayerStore((s) => s.updateLayer);

  const [search, setSearch] = useState('');
  const [expandedKeys, setExpandedKeys] = useState<string[]>(['scene:root', 'cat:camera', 'cat:models', 'cat:lights', 'cat:subsystems']);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [ctx, setCtx] = useState<{ x: number; y: number; node: SceneTreeNode } | null>(null);
  const restoredRef = useRef(false);
  const expandPersistRef = useRef(false);

  // 图层组（type='group'）→ 场景树编组
  const groups = useMemo(
    () => layers.filter((l) => l.type === 'group').map((l) => ({ id: l.id, name: l.name, modelIds: l.modelIds || [] })),
    [layers],
  );

  const input: SceneTreeInput = useMemo(() => ({
    models: models.map((m) => ({ id: m.id, name: m.name, visible: m.visible !== false, format: m.format })),
    lights: lights.map((l) => ({ id: l.id, name: l.name, type: l.type, enabled: l.enabled !== false })),
    pathCameras: pathCameras.map((c) => ({ id: c.id, name: c.name })),
    groups,
    measurements: measurements.map((m, i) => ({ id: m.id, label: `测量 ${i + 1}` })),
    volumes: volumes.map((x) => ({ id: x.id, name: x.name })),
    annotations: annotations.map((x) => ({ id: x.id, name: x.name, kind: x.kind })),
    charts3d: charts3d.map((x) => ({ id: x.id, name: x.name, visible: x.visible })),
    browseRouteLength: browseRoute?.length ?? 0,
    displayRegion: displayRegion as SceneTreeInput['displayRegion'],
    effects: effects as SceneTreeInput['effects'],
    environment: { fogEnabled: environment.fogEnabled, fog: { density: environment.fog?.density } },
    birdsEye: { enabled: birdsEyeCamera.enabled },
    mainCamera: mainCamera.type,
  }), [models, lights, pathCameras, measurements, volumes, annotations, charts3d, groups, browseRoute, displayRegion, effects, environment.fogEnabled, environment.fog?.density, birdsEyeCamera.enabled, mainCamera.type]);

  const tree = useMemo(() => buildSceneTree(input), [input]);
  const shown = useMemo(() => filterTree(tree, search), [tree, search]);
  const nodeMap = useMemo(() => {
    const map = new Map<string, SceneTreeNode>();
    flattenTree(tree).forEach((n) => map.set(n.id, n));
    return map;
  }, [tree]);

  // 恢复展开态（按场景持久化，复用 usePanelPersistence 存档格式）
  useEffect(() => {
    restoredRef.current = false;
    expandPersistRef.current = false;
  }, [sceneId]);
  useEffect(() => {
    if (restoredRef.current || !sceneId) return;
    restoredRef.current = true;
    const saved = readSavedPanelPrefs(sceneId);
    if (saved?.sceneTreeCollapsed?.length) setExpandedKeys(saved.sceneTreeCollapsed);
    expandPersistRef.current = true;
  }, [sceneId]);
  useEffect(() => {
    if (!sceneId || !expandPersistRef.current) return;
    const prev = readSavedPanelPrefs(sceneId);
    writeSavedPanelPrefs(sceneId, {
      ...(prev || { activePanel: null, expandedModelId: null, subObjectUnlocked: {} }),
      sceneTreeCollapsed: expandedKeys,
    });
  }, [expandedKeys, sceneId]);

  // 2026-10-15（P0-③）：编组节点自动展开（新建组后立即可见其子模型）
  useEffect(() => {
    const groupIds = groups.map((g) => `group:${g.id}`);
    if (!groupIds.length) return;
    setExpandedKeys((prev) => {
      const missing = groupIds.filter((id) => !prev.includes(id));
      return missing.length ? [...prev, ...missing] : prev;
    });
  }, [groups]);

  // 反向联动：3D/属性面板选中 → 树高亮 + 展开祖先
  useEffect(() => {
    if (!selectedModelId) return;
    const key = `model:${selectedModelId}`;
    setSelectedKeys((prev) => (prev.includes(key) ? prev : [key]));
    setExpandedKeys((prev) => (prev.includes('cat:models') ? prev : [...prev, 'scene:root', 'cat:models']));
  }, [selectedModelId]);

  // 2026-10-15（P0-⑤ §8.2）：灯光选中 → 树高亮
  useEffect(() => {
    if (!lightSelectedId) return;
    const key = `light:${lightSelectedId}`;
    setSelectedKeys((prev) => (prev.includes(key) ? prev : [key]));
    setExpandedKeys((prev) => (prev.includes('cat:lights') ? prev : [...prev, 'scene:root', 'cat:lights']));
  }, [lightSelectedId]);

  useEffect(() => {
    if (!chart3dSelectedId) return;
    const key = `chart3d:${chart3dSelectedId}`;
    setSelectedKeys((prev) => (prev.includes(key) ? prev : [key]));
    setExpandedKeys((prev) => (prev.includes('cat:charts3d') ? prev : [...prev, 'scene:root', 'cat:charts3d']));
  }, [chart3dSelectedId]);

  const dataNodes = useMemo<DataNode[]>(() => {
    const toNode = (n: SceneTreeNode): DataNode => ({
      key: n.id,
      title: n.label,
      icon: ICONS[n.icon || ''] ?? undefined,
      isLeaf: !n.children?.length,
      children: n.children?.map(toNode),
    });
    return shown.map(toNode);
  }, [shown]);

  const toggleVisible = useCallback((node: SceneTreeNode) => {
    if (node.objectType === 'model' && node.objectId) {
      const next = node.visible === false;
      updateModel(node.objectId, { visible: next });
      apiUpdateModel(node.objectId, { visible: next }).catch(() => {});
    } else if (node.objectType === 'light' && node.objectId) {
      updateLight(node.objectId, { enabled: node.visible === false });
    } else if (node.objectType === 'annotation' && node.objectId) {
      useSceneStore.getState().updateAnnotation(node.objectId, { visible: node.visible === false });
    } else if (node.objectType === 'chart3d' && node.objectId) {
      useSceneStore.getState().updateChart3D(node.objectId, { visible: node.visible === false });
    }
  }, [updateModel, updateLight]);

  const removeSubsystem = useCallback((node: SceneTreeNode) => {
    switch (node.id) {
      case 'sub:sky': setEffects({ sky: { ...effects.sky, enabled: false } }); break;
      case 'sub:fog': setEnvironment({ fogEnabled: false }); break;
      case 'sub:weather': setEffects({ weather: { ...effects.weather, type: 'clear' } }); break;
      case 'sub:roam': useSceneStore.getState().setPathCameras([]); setActivePathCamera(null); break;
      case 'sub:fpsRoute': setBrowseRoute([]); break;
      case 'sub:displayRegion': setDisplayRegion(null); break;
      case 'sub:dayNight': setEffects({ dayNight: { ...effects.dayNight, enabled: false } }); break;
      default: break;
    }
  }, [effects.sky, effects.weather, effects.dayNight, setEffects, setEnvironment, setActivePathCamera, setBrowseRoute, setDisplayRegion]);

  const handleAction = useCallback((action: ContextMenuAction['key'], node: SceneTreeNode) => {
    switch (action) {
      case 'rename': {
        setEditingId(node.id);
        setRenameValue(node.label);
        break;
      }
      case 'toggleVisible':
        toggleVisible(node);
        break;
      case 'locate': {
        if (node.objectId) {
          selectModel(node.objectId);
          window.dispatchEvent(new CustomEvent('dtwin:focus-model', { detail: { modelId: node.objectId } }));
        }
        break;
      }
      case 'enterCamera': {
        if (node.objectId) {
          setActivePathCamera(node.objectId);
          setActivePanel('camera');
          // 2026-10-15（P0-④ §3.5）：进入相机——编辑器视角落到该相机位姿（目标=视线方向 10m）
          const cam = useSceneStore.getState().pathCameras.find((c) => c.id === node.objectId);
          if (cam) {
            const pose = mirrorWaypointsFromCameras([cam])[0];
            cameraControlsRegistry.applySavedCamera?.({ position: pose.position, target: pose.target });
          }
        }
        break;
      }
      case 'delete': {
        if (node.objectType === 'model' && node.objectId) {
          deleteModelsByIds([node.objectId]);
        } else if (node.objectType === 'group' && node.objectId) {
          removeLayer(node.objectId);
        } else if (node.objectType === 'light' && node.objectId) {
          removeLight(node.objectId);
        } else if (node.objectType === 'annotation' && node.objectId) {
          useSceneStore.getState().removeAnnotation(node.objectId);
        } else if (node.objectType === 'chart3d' && node.objectId) {
          useSceneStore.getState().removeChart3D(node.objectId);
          useSelectionStore.getState().selectChart3D(null);
        } else if (node.objectType === 'camera' && node.id.startsWith('cam:path:') && node.objectId) {
          removePathCamera(node.objectId);
        } else if (node.kind === 'subsystem') {
          removeSubsystem(node);
        }
        break;
      }
      default: break;
    }
  }, [toggleVisible, selectModel, setActivePathCamera, setActivePanel, removeLayer, removeLight, removePathCamera, removeSubsystem]);

  const commitRename = useCallback((node: SceneTreeNode) => {
    const name = renameValue.trim();
    setEditingId(null);
    if (!name || name === node.label) return;
    if (node.objectType === 'model' && node.objectId) {
      updateModel(node.objectId, { name });
      apiUpdateModel(node.objectId, { name }).catch(() => {});
    } else if (node.objectType === 'light' && node.objectId) {
      updateLight(node.objectId, { name });
    } else if (node.objectType === 'group' && node.objectId) {
      updateLayer(node.objectId, { name });
    } else if (node.objectType === 'chart3d' && node.objectId) {
      useSceneStore.getState().updateChart3D(node.objectId, { name });
    } else if (node.objectType === 'camera' && node.id.startsWith('cam:path:') && node.objectId) {
      useSceneStore.getState().updatePathCamera(node.objectId, { name });
    }
  }, [renameValue, updateModel, updateLight, updateLayer]);

  const handleSelect = useCallback((keys: React.Key[]) => {
    const ids = keys.map(String);
    setSelectedKeys(ids);
    const last = ids[ids.length - 1];
    const node = last ? nodeMap.get(last) : null;
    if (!node) return;
    if (node.id === 'scene:root') {
      setActivePanel('scene');
      return;
    }
    if (node.kind === 'subsystem' && node.panel) {
      setActivePanel(node.panel);
      return;
    }
    if (node.objectType === 'model' && node.objectId) {
      selectModel(node.objectId);
    } else if (node.objectType === 'camera' && node.id === 'cam:birdseye') {
      setActivePanel('birdsEye');
    } else if (node.objectType === 'camera' && node.id.startsWith('cam:path:') && node.objectId) {
      setActivePathCamera(node.objectId);
      setActivePanel('camera');
    } else if (node.objectType === 'camera') {
      setActivePanel('scene');
    } else if (node.objectType === 'measure') {
      setActivePanel('measurement');
    } else if (node.objectType === 'light' && node.objectId) {
      // 2026-10-15（P0-⑤ §8.2）：灯光节点 ↔ 场景灯光 gizmo 选中联动
      selectLight(node.objectId);
      setActivePanel('lights');
    } else if (node.objectType === 'annotation' && node.objectId) {
      // 2026-10-17（标绘层 S2）：标绘节点 ↔ 手柄/面板联动
      useSelectionStore.getState().selectAnnotation(node.objectId);
      setActivePanel('environment');
    } else if (node.objectType === 'chart3d' && node.objectId) {
      useSelectionStore.getState().selectChart3D(node.objectId);
      useSceneStore.getState().selectModel(null);
      setActivePanel('chart3d');
    }
  }, [nodeMap, selectModel, setActivePathCamera, setActivePanel]);

  // 多选编组 / 解散
  const selectedModelIds = useMemo(
    () => selectedKeys.map((k) => nodeMap.get(k)).filter((n) => n?.objectType === 'model' && n.objectId).map((n) => n!.objectId!),
    [selectedKeys, nodeMap],
  );
  const selectedGroup = useMemo(
    () => selectedKeys.map((k) => nodeMap.get(k)).find((n) => n?.objectType === 'group') ?? null,
    [selectedKeys, nodeMap],
  );
  const handleGroup = useCallback(() => {
    if (selectedModelIds.length < 2) return;
    const n = layers.filter((l) => l.type === 'group').length + 1;
    addLayer({ name: `编组${n}`, type: 'group', visible: true, locked: false, modelIds: [...selectedModelIds] });
    message.success(`已编组 ${selectedModelIds.length} 个模型`);
  }, [selectedModelIds, layers, addLayer]);
  const handleUngroup = useCallback(() => {
    if (!selectedGroup?.objectId) return;
    removeLayer(selectedGroup.objectId);
    message.success('已解散编组');
  }, [selectedGroup, removeLayer]);

  // 拖拽重排（仅模型节点之间；分类/子系统不可拖）
  const handleDrop = useCallback((info: any) => {
    const dragId = String(info.dragNode?.key ?? '');
    const dropId = String(info.node?.key ?? '');
    const dragNode = nodeMap.get(dragId);
    const dropNode = nodeMap.get(dropId);
    if (!dragNode || !dropNode) return;
    if (dragNode.objectType !== 'model' || !dragNode.objectId) return;
    if (!info.dropToGap) return; // 不支持拖入编组（P1）
    const order = modelOrderInTree(tree);
    const dropIdx = dropNode.objectType === 'model' && dropNode.objectId
      ? order.indexOf(dropNode.objectId)
      : -1;
    if (dropIdx < 0) return;
    const overIndex = dropIdx + (info.dropPosition > 0 ? 1 : 0);
    const next = reorderByDrop(order, dragNode.objectId, overIndex);
    if (next.join('|') === order.join('|')) return;
    const byId = new Map(models.map((m) => [m.id, m]));
    setModels(next.map((id) => byId.get(id)).filter(Boolean) as typeof models);
    message.success('已调整模型顺序');
  }, [nodeMap, tree, models, setModels]);

  const renderTitle = useCallback((node: DataNode) => {
    const n = nodeMap.get(String(node.key));
    if (!n) return node.title as React.ReactNode;
    const isEditing = editingId === n.id;
    const isSubsystem = n.kind === 'subsystem';
    const isCategory = n.kind === 'category';
    return (
      <div
        data-testid={`stn-${n.id}`}
        data-node-kind={n.kind}
        style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', minHeight: 22 }}
        onDoubleClick={(e) => {
          if (n.kind === 'category' || isSubsystem) return;
          e.stopPropagation();
          setEditingId(n.id);
          setRenameValue(n.label);
        }}
        onContextMenu={(e) => {
          if (isCategory) return;
          e.preventDefault();
          setCtx({ x: e.clientX, y: e.clientY, node: n });
        }}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }}>
          {isEditing ? (
            <Input
              size="small"
              autoFocus
              data-testid={`st-rename-${n.id}`}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onPressEnter={() => commitRename(n)}
              onBlur={() => commitRename(n)}
              // 2026-10-15：阻止键盘事件冒泡到 antd Tree——rc-tree 的 Enter 会切换展开态
              // （重命名按 Enter 会折叠树；ArrowLeft/Right 会移动树焦点），Escape 仅退出编辑
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Escape') setEditingId(null);
              }}
              onClick={(e) => e.stopPropagation()}
              style={{ height: 20, fontSize: 12 }}
            />
          ) : (
            <>
              {n.label}
              {typeof n.count === 'number' && n.kind === 'category' && (
                <Text style={{ fontSize: 11, color: '#888', marginLeft: 4 }}>({n.count})</Text>
              )}
            </>
          )}
        </span>
        {isSubsystem && (
          <Space size={4} style={{ flexShrink: 0 }}>
            {(n.badges || []).map((b, i) => (
              <Text key={i} data-testid={`st-badge-${n.id}`} style={{ fontSize: 10, color: '#9254de', background: 'rgba(146,84,222,0.15)', borderRadius: 3, padding: '0 4px' }}>
                {b}
              </Text>
            ))}
          </Space>
        )}
        {(n.objectType === 'model' || n.objectType === 'light') && (
          <Tooltip title={n.visible === false ? '显示' : '隐藏'}>
            <Button
              type="text"
              size="small"
              data-testid={`st-eye-${n.objectId}`}
              icon={n.visible === false ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              onClick={(e) => { e.stopPropagation(); toggleVisible(n); }}
              style={{ width: 20, height: 20, flexShrink: 0, color: n.visible === false ? '#666' : '#1890ff' }}
            />
          </Tooltip>
        )}
        {n.removable && !isCategory && (
          <Popconfirm
            title={isSubsystem ? `移除「${n.label}」？` : '删除该对象？'}
            okText="删除"
            cancelText="取消"
            onConfirm={() => handleAction('delete', n)}
          >
            <Button
              type="text"
              size="small"
              data-testid={`st-del-${n.id}`}
              onClick={(e) => e.stopPropagation()}
              style={{ width: 20, height: 20, flexShrink: 0, color: '#ff7875' }}
            >
              ×
            </Button>
          </Popconfirm>
        )}
      </div>
    );
  }, [nodeMap, editingId, renameValue, commitRename, toggleVisible, handleAction]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }} data-testid="scene-tree">
      <div style={{ padding: '8px 10px 4px' }}>
        <Input
          size="small"
          allowClear
          data-testid="scene-tree-search"
          prefix={<SearchOutlined style={{ fontSize: 11 }} />}
          placeholder="搜索场景对象"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Space size={6} style={{ marginTop: 6 }}>
          <Button
            size="small"
            data-testid="scene-tree-group"
            icon={<GroupOutlined />}
            disabled={selectedModelIds.length < 2}
            onClick={handleGroup}
          >
            编组
          </Button>
          <Button
            size="small"
            data-testid="scene-tree-ungroup"
            icon={<UngroupOutlined />}
            disabled={!selectedGroup}
            onClick={handleUngroup}
          >
            解散
          </Button>
          {selectedModelIds.length >= 2 && <Text style={{ fontSize: 11, color: '#888' }}>已选 {selectedModelIds.length} 个模型</Text>}
        </Space>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: '0 6px 8px' }}>
        {shown.length === 0 ? (
          <Empty description="暂无匹配对象" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Tree
            treeData={dataNodes}
            titleRender={renderTitle}
            showIcon
            multiple
            draggable={{ icon: false }}
            blockNode
            expandedKeys={expandedKeys}
            onExpand={(keys) => setExpandedKeys(keys.map(String))}
            selectedKeys={selectedKeys}
            onSelect={handleSelect}
            onDrop={handleDrop}
            style={{ background: 'transparent', color: '#e0e0e0' }}
          />
        )}
      </div>
      {ctx && (
        <SceneTreeContextMenu
          x={ctx.x}
          y={ctx.y}
          node={ctx.node}
          onAction={handleAction}
          onClose={() => setCtx(null)}
        />
      )}
    </div>
  );
}
